import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Linkedin, MapPin, Phone, Mail, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONTACT_ADDRESS, CONTACT_EMAIL, CONTACT_PHONE_DISPLAY } from '../utils/constants';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Full Truck Load', href: '#fleet' },
      { name: 'Part Load (LTL)', href: '#services' },
      { name: 'E-Commerce Delivery', href: '#services' },
      { name: 'Industrial Logistics', href: '#services' },
      { name: 'Customized Solutions', href: '/#book' }
    ],
    company: [
      { name: 'Our Fleet', href: '#fleet' },
      { name: 'Network Hubs', href: '#routes' },
      { name: 'About PkRouteX', href: '/about' },
      { name: 'Track Order', href: '/track' }
    ]
  };

  return (
    <footer className="relative bg-brand-black pt-16 sm:pt-24 pb-10 sm:pb-12 overflow-hidden border-t border-white/5">
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] sm:w-[800px] h-[200px] sm:h-[300px] bg-brand-orange/10 blur-[120px] rounded-full pointer-events-none opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-8 mb-12 sm:mb-16 md:mb-20">

          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-4">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <img
                src="/favicon.png"
                alt="PKROUTEX Logo"
                className="h-10 sm:h-12 md:h-16 w-auto object-contain mb-6 sm:mb-8"
              />
              <p className="text-gray-300 mb-6 sm:mb-8 max-w-sm leading-relaxed text-sm">
                Redefining the landscape of Indian logistics through technology-driven routes, cinematic precision, and a premium fleet that never stops.
              </p>
              <div className="flex gap-3 sm:gap-4">
                {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
                  <motion.a key={i} href="#"
                    whileHover={{ y: -6, scale: 1.15, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 hover:border-brand-orange/50 hover:bg-brand-orange/10 transition-colors group"
                  >
                    <Icon size={16} className="text-gray-300 group-hover:text-brand-orange transition-colors" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-black mb-5 sm:mb-8 uppercase tracking-[0.2em] text-[10px]">Services</h4>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.services.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-gray-400 hover:text-brand-orange text-sm flex items-center group transition-colors">
                    <ChevronRight size={14} className="mr-0 opacity-0 group-hover:opacity-100 group-hover:mr-2 transition-all shrink-0" />
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Access */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-black mb-5 sm:mb-8 uppercase tracking-[0.2em] text-[10px]">Quick Access</h4>
            <ul className="space-y-3 sm:space-y-4">
              {footerLinks.company.map((item) => (
                <li key={item.name}>
                  <a href={item.href} className="text-gray-400 hover:text-brand-orange text-sm flex items-center group transition-colors">
                    <ChevronRight size={14} className="mr-0 opacity-0 group-hover:opacity-100 group-hover:mr-2 transition-all shrink-0" />
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="sm:col-span-2 lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-white/5 border border-white/8 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-orange/5 blur-[40px] rounded-full" />
              <h4 className="text-white font-black mb-5 sm:mb-8 uppercase tracking-[0.2em] text-[10px]">Direct Support</h4>
              <ul className="space-y-4 sm:space-y-6 relative z-10">
                <li className="flex gap-3 sm:gap-4">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center shrink-0">
                    <MapPin className="text-brand-orange" size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-1">Corporate HQ</p>
                    <p className="text-xs text-gray-300 leading-relaxed">{CONTACT_ADDRESS}</p>
                  </div>
                </li>
                <li className="flex gap-3 sm:gap-4 items-center">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center shrink-0">
                    <Phone className="text-brand-orange" size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-1">24/7 Helpline</p>
                    <p className="text-sm text-white font-bold">{CONTACT_PHONE_DISPLAY}</p>
                  </div>
                </li>
                <li className="flex gap-3 sm:gap-4 items-center">
                  <div className="w-9 sm:w-10 h-9 sm:h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center shrink-0">
                    <Mail className="text-brand-orange" size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-1">Inquiries</p>
                    <p className="text-sm text-white font-bold break-all">{CONTACT_EMAIL}</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        <div className="pt-8 sm:pt-12 border-t border-white/5 flex flex-col items-center gap-4 sm:gap-6 text-center">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 md:gap-8">
            <p className="text-gray-400 text-xs font-medium">© {currentYear} PKRouteX Logistics Solutions Pvt. Ltd.</p>
            <div className="flex gap-4 sm:gap-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-[10px] uppercase font-black tracking-widest">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-[10px] uppercase font-black tracking-widest">Terms</Link>
              <Link to="/compliance" className="text-gray-400 hover:text-white transition-colors text-[10px] uppercase font-black tracking-widest">Compliance</Link>
            </div>
          </div>
          <p className="text-gray-500 text-[10px] uppercase font-black tracking-widest">
            Powered by{' '}
            <a
              href="https://aitechpulze.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-orange hover:text-brand-orange-light transition-colors underline underline-offset-2"
            >
              AiTechPulze
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
